"use client";

import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Hero() {
  const container = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".al-eyebrow", {
        y: 16
      });
      gsap.set(".al-headline", {
        y: 32
      });
      gsap.set(".al-card", {
        y: 40
      });
      gsap.set(".al-caption", {
        y: 12
      });
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out",
          duration: 0.7
        }
      });
      tl.to(".al-eyebrow", {
        opacity: 1,
        y: 0
      }).to(".al-headline", {
        opacity: 1,
        y: 0
      }, "-=0.5").to(".al-card", {
        opacity: 1,
        y: 0
      }, "-=0.4").to(".al-caption", {
        opacity: 1,
        y: 0
      }, "-=0.4");
      if (bgRef.current) {
        gsap.to(bgRef.current, {
          yPercent: -8,
          scale: 1.02,
          ease: "none",
          scrollTrigger: {
            trigger: el,
            start: "top top",
            end: "bottom top",
            scrub: true
          }
        });
      }
      const refresh = () => ScrollTrigger.refresh();
      requestAnimationFrame(refresh);
      if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
        once: true
      });
      document.fonts?.ready.then(refresh);
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".al-eyebrow", ".al-headline", ".al-card", ".al-caption"], {
        opacity: 1,
        y: 0
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="31401" ref={container} id="automotivemanufacturinghero" className="relative isolate overflow-hidden bg-background text-foreground" style={{
    minHeight: "88vh"
  }}>
      {}
      <div ref={bgRef} aria-hidden="true" className="absolute inset-0 -z-20 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_spraying_systems.png)]" />

      {}
      <div aria-hidden="true" className="absolute inset-0 -z-10" style={{
      background: "linear-gradient(215deg, hsl(215 80% 20% / 0.75) 0%, hsl(215 90% 45% / 0.55) 40%, hsl(220 22% 6% / 0.95) 100%)"
    }} />

      {}
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 -z-10 h-[55%]" style={{
      background: "linear-gradient(to top, hsl(220 22% 6% / 0.95) 0%, hsl(220 22% 6% / 0.65) 50%, hsl(220 22% 6% / 0) 100%)"
    }} />

      <div className="relative mx-auto flex min-h-[88vh] w-full max-w-[1360px] flex-col justify-end" style={{
      paddingInline: "clamp(20px, 4vw, 56px)",
      paddingTop: "clamp(140px, 16vw, 200px)",
      paddingBottom: "clamp(80px, 10vw, 140px)"
    }}>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8 lg:items-end">
          {}
          <div className="lg:col-span-8">
            <p className="al-eyebrow font-default uppercase" style={{
            opacity: 0,
            color: "hsl(210 20% 98%)",
            letterSpacing: "0.22em",
            fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
            fontWeight: 500,
            lineHeight: 1
          }}>
              {"Especialistas em Pulverização"}
            </p>

            <h1 className="al-headline font-default mt-6" style={{
            opacity: 0,
            color: "hsl(210 20% 98%)",
            fontSize: "clamp(3.25rem, 7.8vw, 7.5rem)",
            fontWeight: 500,
            letterSpacing: "-0.035em",
            lineHeight: 0.94
          }}>
              {"Especialistas em Pulverização desde 1937"}
            </h1>
          </div>

          {}
          <div className="lg:col-span-4 lg:-mt-24 xl:-mt-32">
            <div className="al-card rounded-lg border p-8" style={{
            opacity: 0,
            backgroundColor: "hsl(220 22% 6% / 0.4)",
            backdropFilter: "blur(24px) saturate(140%)",
            WebkitBackdropFilter: "blur(24px) saturate(140%)",
            borderColor: "hsl(210 10% 85% / 0.2)"
          }}>
              <p className="font-default" style={{
              color: "hsl(210 20% 98%)",
              fontSize: "clamp(1.0625rem, 1.35vw, 1.25rem)",
              lineHeight: 1.55,
              letterSpacing: "-0.005em",
              fontWeight: 400
            }}>
                {"Representantes exclusivos da Spraying Systems Co. em Portugal desde 1993."}
              </p>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button asChild className="group h-[52px] rounded-lg pl-7 pr-2 text-[0.9375rem] font-medium" style={{
                backgroundColor: "hsl(215 90% 45%)",
                color: "hsl(210 20% 98%)"
              }}>
                  <Link to="/ContactsPage">
                    <span>{"Consulte-nos"}</span>
                    <span className="ml-1 inline-flex h-8 w-8 items-center justify-center rounded-lg transition-transform duration-300 ease-out group-hover:-rotate-45" style={{
                    backgroundColor: "hsl(210 20% 98%)",
                    color: "hsl(215 90% 45%)"
                  }}>
                      <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-[52px] rounded-lg px-6 text-[0.9375rem] font-medium border" style={{
                backgroundColor: "hsl(210 10% 92% / 0.1)",
                borderColor: "hsl(210 10% 85% / 0.2)",
                color: "hsl(210 20% 98%)"
              }}>
                  <Link to="/ProductsPage">{"Ver Produtos"}</Link>
                </Button>
              </div>

              <div className="mt-8 border-t pt-6" style={{
              borderColor: "hsl(210 10% 85% / 0.2)"
            }}>
                <p className="al-caption font-default" style={{
                opacity: 0,
                color: "hsl(215 12% 70%)",
                fontSize: "clamp(0.8125rem, 0.88vw, 0.9375rem)",
                letterSpacing: "0.02em",
                lineHeight: 1.45
              }}>
                  {"Soluções de engenharia industrial de alta precisão."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}